import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogHeader,
  DialogBody,
  Button,
} from "@material-tailwind/react";
import {
  XMarkIcon,
  DocumentTextIcon,
  EyeIcon,
  ArrowDownTrayIcon,
  CheckCircleIcon,
  XCircleIcon,
} from "@heroicons/react/24/solid";
import { useDispatch, useSelector } from "react-redux";
import {
  getUploadedProof,
  reviewDeliveryProof,
} from "@/store/slices/newDeliverySlice";
import Swal from "sweetalert2";
import { useTranslation } from "react-i18next";

function nameFromUrl(url) {
  try {
    const u = new URL(url);
    const last = u.pathname.split("/").pop();
    return decodeURIComponent(last || "Receipt");
  } catch {
    const last = String(url || "").split("/").pop();
    return last || "Receipt";
  }
}

function NewStampBadge({ t }) {
  return (
    <div
      className="absolute top-2 -right-8 rotate-45 bg-red-600 text-white text-[10px] font-bold px-8 py-0.5 shadow-md z-10 select-none pointer-events-none"
      title="Newly uploaded"
    >
      {t("new_badge")}
    </div>
  );
}

export default function ViewUploadedProofModal({
  open,
  onClose,
  deliveryAssignedOrderId,
}) {
  const dispatch = useDispatch();
  const { t } = useTranslation('order');
  const { uploaded_proofes } = useSelector((state) => state.delivery);

  const [isBootLoading, setIsBootLoading] = useState(false);

  useEffect(() => {
    if (!open || !deliveryAssignedOrderId) return;

    let cancelled = false;

    setIsBootLoading(true);

    dispatch(getUploadedProof({ id: deliveryAssignedOrderId }))
      .unwrap()
      .catch(() => { })
      .finally(() => {
        if (!cancelled) setIsBootLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, deliveryAssignedOrderId, dispatch]);

  const list = Array.isArray(uploaded_proofes) ? uploaded_proofes : [];

  const { images, receipts } = useMemo(() => {
    const imgs = list
      .filter((p) => p?.type === "image" && p?.file)
      .map((p) => ({
        id: p.id,
        file: p.file,
        timestamp: p?.created_at || p?.updated_at || "",
        stamp: p?.upload_stamp,
      }));

    const recs = list
      .filter((p) => p?.type === "receipt" && p?.file)
      .map((p) => ({
        id: p.id,
        file: p.file,
        name: nameFromUrl(p.file),
        stamp: p?.upload_stamp,
      }));

    return { images: imgs, receipts: recs };
  }, [list]);

  const firstRemark = useMemo(
    () => list.find((p) => p?.remark)?.remark || "",
    [list]
  );

  const proofRejectCount = useMemo(() => {
    return Number(list[0]?.proof_reject_count || 0);
  }, [list]);

  const isReuploadRequired = useMemo(() => {
    return Boolean(list[0]?.is_reupload_required);
  }, [list]);

  // Check if every proof in the list has admin_status as "approved"
  const isAllApproved = useMemo(() => {
    return list.length > 0 && list.every((p) => p?.admin_status === "approved");
  }, [list]);

  // Show status once
  const reviewStatus = useMemo(
    () => list.find((p) => p?.admin_status)?.admin_status || "pending",
    [list]
  );

  const rejectedReason = useMemo(
    () => list.find((p) => p?.rejected_reason)?.rejected_reason || "",
    [list]
  );

  // const canApprove = !isReuploadRequired;

  const canApprove = !isReuploadRequired && !isAllApproved;

  const canReject = !isReuploadRequired;

  const hasProofs = images.length > 0 || receipts.length > 0;

  const handleDownload = async (url, filename) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = filename || "download";

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Download failed:", error);
      window.open(url, "_blank");
    }
  };

  const handleReview = async (status) => {
    try {
      // Close dialog first so Swal appears on top
      onClose?.();

      let rejectedReasonValue = "";

      if (status === "rejected") {
        const result = await Swal.fire({
          title: t('reject_delivery_proof_title'),
          input: "textarea",
          inputLabel: t('reason_label'),
          inputPlaceholder: t('enter_rejection_reason_placeholder'),
          inputAttributes: {
            maxlength: 300,
            autocapitalize: "off",
            autocorrect: "off",
          },
          showCancelButton: true,
          confirmButtonText: t('reject_button'),
          confirmButtonColor: "#dc2626",
          cancelButtonText: t('cancel_button'),
          allowOutsideClick: false,
          allowEscapeKey: false,
          customClass: {
            popup: "!z-[99999]",
            container: "!z-[99999]",
          },
          didOpen: () => {
            const textarea = Swal.getInput();
            if (textarea) {
              textarea.addEventListener("input", () => {
                if (textarea.value.length > 300) {
                  textarea.value = textarea.value.slice(0, 300);
                }
              });
            }
          },
          inputValidator: (value) => {
            const trimmed = value?.trim() || "";

            if (!trimmed) {
              return t('rejection_reason_required');
            }

            if (trimmed.length < 3) {
              return t('reason_min_3_chars');
            }

            if (trimmed.length > 300) {
              return t('reason_max_300_chars');
            }

            return null;
          },
        });

        if (!result.isConfirmed) return;

        rejectedReasonValue = result.value.trim();
      } else {
        const result = await Swal.fire({
          title: t('approve_delivery_proof_title'),
          icon: "question",
          showCancelButton: true,
          confirmButtonText: t('approve_button'),
          confirmButtonColor: "#16a34a",
          cancelButtonText: t('cancel_button'),
          allowOutsideClick: false,
          allowEscapeKey: false,
          customClass: {
            popup: "!z-[99999]",
            container: "!z-[99999]",
          },
        });

        if (!result.isConfirmed) return;
      }

      const response = await dispatch(
        reviewDeliveryProof({
          delivery_assigned_orders_id: deliveryAssignedOrderId,
          admin_status: status,
          rejected_reason: rejectedReasonValue,
        })
      ).unwrap();

      if (!response?.status) {
        throw new Error(response?.message || t('no_confirmed_delivery_proofs'));
      }

      await Swal.fire({
        icon: "success",
        title: status === "approved" ? t('approved_status') : t('rejected_status'),
        text:
          status === "approved"
            ? t('delivery_proofs_approved_success')
            : t('delivery_proofs_rejected_success'),
        customClass: {
          popup: "!z-[99999]",
          container: "!z-[99999]",
        },
      });

      // Refresh and reopen
      await dispatch(getUploadedProof({ id: deliveryAssignedOrderId }));
      window.location.reload();
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: t('error_title'),
        text: error?.message || t('something_went_wrong'),
        customClass: {
          popup: "!z-[99999]",
          container: "!z-[99999]",
        },
      });
    }
  };

  return (
    <Dialog
      size="lg"
      open={open}
      handler={() => onClose?.()}
      className="rounded-2xl"
    >
      <DialogHeader className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-base font-bold text-gray-900 truncate">
            {t('uploaded_proof_title')}
          </div>

          <div className="text-xs text-gray-500">
            {t('delivery_assigned_order_id_label')}:{" "}
            <span className="font-semibold text-gray-700">
              {deliveryAssignedOrderId || "—"}
            </span>
          </div>

          {firstRemark && (
            <div className="text-xs text-gray-500 mt-1">
              {t('remark_label')}:{" "}
              <span className="font-medium text-gray-700">
                {firstRemark}
              </span>
            </div>
          )}

          {proofRejectCount > 0 && (
            <div className="text-xs text-red-600 mt-1">
              <span className="font-semibold">
                {t('proof_rejection_count_label')}:
              </span>{" "}
              <span className="font-bold">
                {proofRejectCount}
              </span>
            </div>
          )}
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {hasProofs && (
            <>
              {/* <Button
                size="sm"
                className="bg-green-600 hover:bg-green-700"
                onClick={() => handleReview("approved")}
              >
                {t('approve_button')}
              </Button> */}

              <Button
                size="sm"
                disabled={!canApprove}
                className={`bg-green-600 hover:bg-green-700 ${!canApprove ? "opacity-50 cursor-not-allowed" : ""}`}
                onClick={() => handleReview("approved")}
                title={!canApprove ? "Waiting for delivery partner to re-upload proof" : ""}
              >
                {t('approve_button')}
              </Button>

              <Button
                size="sm"
                // color="red"
                disabled={!canReject}
                className={`bg-red-600 hover:bg-red-700 ${!canReject ? "opacity-50 cursor-not-allowed" : ""}`}
                onClick={() => handleReview("rejected")}
                title={!canReject ? "Waiting for delivery partner to re-upload proof" : ""}
              >
                {t('reject_button')}
              </Button>
            </>
          )}

          <Button
            variant="text"
            color="blue-gray"
            onClick={() => onClose?.()}
            className="p-2"
          >
            <XMarkIcon className="h-5 w-5" />
          </Button>
        </div>
      </DialogHeader>

      <DialogBody className="pt-2 overflow-y-auto max-h-[70vh]">
        {isBootLoading ? (
          <div className="flex flex-col items-center justify-center py-12">
            <div className="text-sm text-gray-500 animate-pulse">
              {t('loading_uploaded_proofs')}
            </div>
          </div>
        ) : (
          <div className="space-y-6 pr-2">
            {/* Review Status */}
            {hasProofs && (
              <div
                className={`rounded-xl border p-4 ${reviewStatus === "approved"
                  ? "border-green-200 bg-green-50"
                  : reviewStatus === "rejected"
                    ? "border-red-200 bg-red-50"
                    : "border-yellow-200 bg-yellow-50"
                  }`}
              >
                <div className="flex items-center gap-2">
                  {reviewStatus === "approved" ? (
                    <CheckCircleIcon className="h-5 w-5 text-green-600" />
                  ) : reviewStatus === "rejected" ? (
                    <XCircleIcon className="h-5 w-5 text-red-600" />
                  ) : null}

                  <span className="text-sm font-semibold capitalize">
                    {t('status_label')}: {reviewStatus}
                  </span>
                </div>

                {reviewStatus === "rejected" && rejectedReason && (
                  <div className="mt-3 text-sm text-red-700 break-all">
                    <span className="font-semibold">{t('reason_label')}:</span> {rejectedReason}
                  </div>
                )}
              </div>
            )}

            {/* Images */}
            <div>
              <div className="flex items-center justify-between gap-3 mb-4 sticky top-0 bg-white z-10 py-1">
                <h3 className="text-sm sm:text-base font-semibold text-gray-800">
                  {t('delivery_proof_images_title')}
                </h3>

                <span className="text-xs text-gray-500 bg-gray-100 px-3 py-1 rounded-full shrink-0">
                  {t('images_uploaded_count', { count: images.length })}
                </span>
              </div>

              {images.length === 0 ? (
                <div className="text-sm text-gray-500 bg-gray-50 border border-dashed border-gray-200 rounded-xl p-4">
                  {t('no_images_uploaded')}
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-5">
                  {images.map((img) => (
                    <button
                      type="button"
                      key={img.id}
                      onClick={() =>
                        window.open(img.file, "_blank", "noopener,noreferrer")
                      }
                      className="relative bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden text-left hover:shadow-md transition"
                    >
                      {img.stamp === "new" && <NewStampBadge t={t} />}
                      <img
                        src={img.file}
                        alt="uploaded"
                        className="h-40 w-full object-cover"
                        loading="lazy"
                      />

                      <div className="text-[10px] text-gray-500 p-2 font-medium truncate">
                        {img.timestamp || "—"}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Receipts */}
            <div>
              <div className="flex items-center justify-between gap-3 mb-4 sticky top-0 bg-white z-10 py-1">
                <h3 className="text-sm sm:text-base font-semibold text-gray-800">
                  {t('delivery_receipts_title')}
                </h3>

                <span className="text-xs text-gray-500 bg-gray-100 px-3 py-1 rounded-full shrink-0">
                  {t('receipts_uploaded_count', { count: receipts.length })}
                </span>
              </div>

              {receipts.length === 0 ? (
                <div className="text-sm text-gray-500 bg-gray-50 border border-dashed border-gray-200 rounded-xl p-4">
                  {t('no_receipts_uploaded')}
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {receipts.map((receipt) => (
                    <div
                      key={receipt.id}
                      className="relative overflow-hidden flex items-center justify-between gap-3 bg-[#FFF1F1] p-3 rounded-lg border border-[#FFDEDE]"
                    >
                      {receipt.stamp === "new" && <NewStampBadge t={t} />}
                      <div className="flex items-center gap-2 min-w-0">
                        <DocumentTextIcon className="h-5 w-5 text-gray-700 shrink-0" />

                        <div className="min-w-0">
                          <div className="text-xs font-semibold text-gray-800 truncate">
                            {receipt.name}
                          </div>

                          <div className="text-[10px] text-gray-500 truncate">
                            {receipt.file}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-1 shrink-0">
                        <button
                          type="button"
                          className="p-2 rounded-md hover:bg-white/70 transition"
                          onClick={() =>
                            window.open(receipt.file, "_blank", "noopener,noreferrer")
                          }
                        >
                          <EyeIcon className="h-5 w-5 text-[#F4B11F]" />
                        </button>

                        <button
                          type="button"
                          className="p-2 rounded-md hover:bg-white/70 transition"
                          onClick={() =>
                            handleDownload(receipt.file, receipt.name)
                          }
                        >
                          <ArrowDownTrayIcon className="h-5 w-5 text-blue-600" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </DialogBody>
    </Dialog>
  );
}